import { useMemo } from 'react'
import { MultiPicker } from './MultiPicker'
import type { ID, Member } from '../lib/types'

/**
 * Pick any number of people to filter and export by.
 *
 * The popover itself is MultiPicker; this decides what a "person" is -- active
 * members only, searchable by email as well as name. The two were one
 * component until the Timesheet needed the same control for clients, and a
 * second copy of the toggling, dismissal and label logic was not worth having.
 */
export function PeoplePicker({
  team,
  selected,
  onChange,
}: {
  team: readonly Member[]
  selected: readonly ID[]
  onChange: (ids: ID[]) => void
}) {
  const items = useMemo(
    () =>
      team
        /*
         * Former members are INCLUDED. Removing somebody keeps the hours they
         * logged -- the confirmation says so -- and a filter that could not
         * name them meant their time showed up in every total but could never
         * be looked at on its own. Reporting on a departed person's work is a
         * normal thing to need at the end of a quarter.
         *
         * A pending invite is left out: no account, so no time to report on.
         */
        .filter((m) => m.status !== 'pending')
        // Email is searchable but never shown: two people called Chen are told
        // apart by typing the address, not by reading one.
        .map((m) => ({
          id: m.id,
          name: m.status === 'retired' ? m.name + ' (former)' : m.name,
          search: m.email,
        })),
    [team],
  )

  return (
    <MultiPicker
      items={items}
      selected={selected}
      onChange={onChange}
      allLabel="Team — everyone"
      unit={['person', 'people']}
      searchPlaceholder="Search people…"
      ariaLabel="Filter by person"
      emptyText="Nobody in the workspace yet."
    />
  )
}
